/**
 * Reporting periods, cut in the household's timezone (spec v2 16, plan step 1.4).
 *
 * EVERY report goes through these functions. The reason is a single class of bug: a month
 * cut with the device clock puts a transaction made at 23:59 on the last day of the month
 * into the next month for a household east of UTC — the family's August spending would
 * silently move to September, and two screens reading the same ledger would disagree.
 *
 * The range is half-open: `[fromUtc, toUtc)`. The end is the first instant of the NEXT
 * month, never "the last instant of this one" — there is no last instant to name, and
 * `occurred_at <= toUtc` would double-count midnight.
 *
 * No dependency: `Intl.DateTimeFormat` already knows every timezone rule, including the DST
 * transitions that make "local midnight" a moving target.
 */
const WALL_CLOCK_FORMATTERS = new Map();
function wallClockFormatter(timeZone) {
    const cached = WALL_CLOCK_FORMATTERS.get(timeZone);
    if (cached !== undefined)
        return cached;
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    WALL_CLOCK_FORMATTERS.set(timeZone, formatter);
    return formatter;
}
/** The wall-clock fields `timeZone` shows for an instant. */
function wallClockFields(instant, timeZone) {
    const parts = wallClockFormatter(timeZone).formatToParts(new Date(instant));
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        return part === undefined ? 0 : Number(part.value);
    };
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        // `hour12: false` prints midnight as "24" in some engines (hourCycle h24) — normalise.
        hour: read('hour') % 24,
        minute: read('minute'),
        second: read('second'),
    };
}
/** How far `timeZone` is east of UTC at `instant`, in milliseconds. */
function zoneOffsetMs(instant, timeZone) {
    const fields = wallClockFields(instant, timeZone);
    const asUtc = Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, fields.second);
    return asUtc - instant;
}
/**
 * The instant local midnight of the 1st falls on.
 *
 * Two passes, because the answer depends on itself: the offset is a property of the instant
 * we are trying to find. The first pass reads the offset near the target, the second reads
 * it AT the candidate and corrects when a DST transition sits in between.
 */
function startOfMonthUtc(year, month, timeZone) {
    const wallClock = Date.UTC(year, month - 1, 1, 0, 0, 0);
    const guess = wallClock - zoneOffsetMs(wallClock, timeZone);
    return wallClock - zoneOffsetMs(guess, timeZone);
}
function assertPeriod(year, month) {
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
        throw new Error(`periodRange: invalid period ${year}-${month} (month must be 1-12)`);
    }
}
/**
 * The instants bounding a calendar month in the household timezone.
 *
 * ```ts
 * periodRange(2026, 8, 'Asia/Ho_Chi_Minh')
 * // { fromUtc: '2026-07-31T17:00:00.000Z', toUtc: '2026-08-31T17:00:00.000Z' }
 * ```
 */
export function periodRange(year, month, timeZone) {
    assertPeriod(year, month);
    const next = shiftPeriod({ year, month }, 1);
    return {
        fromUtc: new Date(startOfMonthUtc(year, month, timeZone)).toISOString(),
        toUtc: new Date(startOfMonthUtc(next.year, next.month, timeZone)).toISOString(),
    };
}
/** The month the household is living right now — not the month the device thinks it is. */
export function currentPeriod(timeZone, now = new Date()) {
    const fields = wallClockFields(now.getTime(), timeZone);
    return { year: fields.year, month: fields.month };
}
/** `months` later (or earlier, when negative), rolling the year over. */
export function shiftPeriod(period, months) {
    assertPeriod(period.year, period.month);
    const monthsFromYearZero = period.year * 12 + (period.month - 1) + months;
    return {
        year: Math.floor(monthsFromYearZero / 12),
        // Modulo of a negative would go negative; the floor above keeps the pair consistent.
        month: (((monthsFromYearZero % 12) + 12) % 12) + 1,
    };
}
/** True when `period` is the same month as `other`. */
export function isSamePeriod(period, other) {
    return period.year === other.year && period.month === other.month;
}
//# sourceMappingURL=periodRange.js.map